import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Device } from '@/types/api';
import type { DeviceSortBy, SortDir } from '@/features/devices/api/devices-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { Select } from '@/components/ui/select';
import { SignalIcon } from '@/components/ui/signal-icon';
import { cn, formatRelativeTime } from '@/lib/utils';
import { deviceStatus } from '../utils/device-status';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';
import { hasSaltConfig } from '@/features/devices/utils/salt-config';
import { SetupModePulseConfigModal } from './setup-mode-pulse-config-modal';

interface SetupModeDeviceGridProps {
  devices: Device[]; // sorted server-side by sortBy/sortDir below
  page: number;
  perPage: number;
  perPageOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  sortBy: DeviceSortBy;
  sortDir: SortDir;
  onSortByChange: (sortBy: DeviceSortBy) => void;
  onSortDirChange: (sortDir: SortDir) => void;
  // Fired after a SALT device is calibrated via the Configure Pulses
  // modal so the parent can refresh its polled list and the note +
  // button drop off the tile.
  onConfigured?: () => void;
}

// Card-grid Setup Mode feed. Optimized for "did this row turn green
// yet?" rather than "what's the reading?" — the table view's columns
// (Reading, Alerts, etc.) are noise during scan-in. Each card is a
// status dot + unit + serial + time, in a responsive 1/2/3/4-column
// grid. Pagination is client-side over the polled list since the
// polling hook already pulls everything.
export function SetupModeDeviceGrid({
  devices,
  page,
  perPage,
  perPageOptions,
  onPageChange,
  onPerPageChange,
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirChange,
  onConfigured,
}: SetupModeDeviceGridProps) {
  // The device whose pulse-config modal is open, if any. Lifted to the
  // grid level so the modal renders once even when the open device gets
  // paginated off the visible page mid-edit.
  const [configuringDevice, setConfiguringDevice] = useState<Device | null>(null);
  // Hide devices that have already had a successful uplink at their
  // current unit. Re-entering Setup Mode after a complete pass should
  // show an empty grid (the user just finished setup; previously-scanned
  // tiles cluttering the page made installers think the prior commit
  // hadn't taken). Persistent per-unit rule via `hasFreshTransmission`:
  // reassigning a device updates `unitAssignedAt`, which makes the
  // device reappear in Setup Mode until its next uplink at the new unit.
  const setupVisibleDevices = useMemo(
    () => devices.filter((d) => !hasFreshTransmission(d)),
    [devices],
  );
  const totalPages = Math.max(1, Math.ceil(setupVisibleDevices.length / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const visibleDevices = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return setupVisibleDevices.slice(start, start + perPage);
  }, [setupVisibleDevices, safePage, perPage]);

  if (setupVisibleDevices.length === 0) {
    return (
      <Card variant="elevated" className="p-10 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Scan a QR code or enter a serial number to add the first meter.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* `items-start` prevents CSS Grid from stretching every tile in
          a row to match the tallest one. A SALT tile with the
          Configure Pulses note is taller than its neighbors; without
          items-start the bare Water Meter card next to it grew to the
          SALT tile's height, padding awkward whitespace into half the
          row. */}
      <div className="grid items-start grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {visibleDevices.map((device) => {
          // SALT-only nudge: when this device is a SALT transmitter that
          // hasn't had its multiplier/UoM set, surface an italic note +
          // button below the tile so the installer can calibrate without
          // navigating into Device Details. Water Meter tiles render the
          // card alone.
          const needsPulseConfig =
            device.deviceType === 'SALTTransmitter' && !hasSaltConfig(device);
          if (!needsPulseConfig) {
            return <SetupDeviceCard key={device.id} device={device} />;
          }
          return (
            <div key={device.id} className="flex flex-col gap-2">
              <SetupDeviceCard device={device} />
              <div className="flex items-center justify-between gap-2 rounded-lg border border-warning-200 dark:border-warning-500/30 bg-warning-50 dark:bg-warning-500/10 px-3 py-2">
                <p className="text-xs italic text-warning-700 dark:text-warning-400 flex-1 leading-snug">
                  Set pulse configuration to track usage.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfiguringDevice(device)}
                >
                  Configure Pulses
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <SetupModePulseConfigModal
        device={configuringDevice}
        isOpen={configuringDevice !== null}
        onClose={() => setConfiguringDevice(null)}
        onSaved={() => onConfigured?.()}
      />

      <Pagination
        currentPage={safePage}
        totalPages={totalPages}
        totalCount={setupVisibleDevices.length}
        onPageChange={onPageChange}
        perPage={perPage}
        perPageOptions={perPageOptions}
        onPerPageChange={onPerPageChange}
        itemNoun={{ singular: 'meter', plural: 'meters' }}
        extra={
          <label className="inline-flex items-center gap-2">
            <span>Sort</span>
            <div className="w-48">
              <Select
                value={`${sortBy}-${sortDir}`}
                onChange={(val) => {
                  const [next, dir] = val.split('-') as [DeviceSortBy, SortDir];
                  onSortByChange(next);
                  onSortDirChange(dir);
                }}
                options={[
                  { value: 'unit-asc', label: 'Unit number (low → high)' },
                  { value: 'unit-desc', label: 'Unit number (high → low)' },
                  { value: 'createdAt-desc', label: 'Recently added' },
                  { value: 'createdAt-asc', label: 'Oldest first' },
                ]}
              />
            </div>
          </label>
        }
      />
    </div>
  );
}

function SetupDeviceCard({ device }: { device: Device }) {
  const navigate = useNavigate();
  const status = deviceStatus(device);
  const isRecognized = status === 'recognized';
  // The chip / "Send Join Request" hint flip on either join or transmission
  // (deviceStatus). The signal value, however, only renders when there's a
  // transmission AT this unit — otherwise a meter that was running in a
  // prior assignment would leak its old signal into the new card. Shared
  // predicate so this rule is enforced identically across the property
  // device card and device detail tiles.
  const isFresh = hasFreshTransmission(device);

  return (
    <button
      type="button"
      onClick={() => navigate(deviceDetailPath(
        device.id,
        device.propertyId ? { kind: 'property', id: device.propertyId } : undefined,
      ))}
      className={cn(
        // min-h reserves the height a tile would have if its identity
        // line wraps onto two lines (long unit name + badge below). This
        // keeps every tile in the grid the same height regardless of
        // whether the badge wraps — without it, "Unit 1" + "SALT" sits
        // taller than a card whose unit name pushed "Water Meter" to a
        // new line, leaving uneven rows.
        'group relative flex items-center gap-3 rounded-xl border bg-white dark:bg-gray-800 px-4 py-3 text-left transition-all min-h-[5rem]',
        'border-gray-200 dark:border-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:-translate-y-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:border-primary-500',
      )}
      aria-label={`${isRecognized ? 'Recognized' : 'Waiting — send join request'} — Unit ${device.unitNumber ?? 'unassigned'}, serial ${device.serialNumber}`}
    >
      {/* Status dot. Recognized = solid green with a soft glow; waiting
          = red dot with a slow pulse so the eye gets drawn to the rows
          that haven't finished commissioning. */}
      <span
        aria-hidden="true"
        className={cn(
          'relative flex-shrink-0 w-3 h-3 rounded-full',
          isRecognized
            ? 'bg-success-500 shadow-[0_0_8px_rgba(34,226,94,0.45)]'
            : 'bg-error-500',
        )}
      >
        {!isRecognized && (
          <span className="absolute inset-0 rounded-full bg-error-500/60 animate-ping" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        {/* flex-wrap lets the device-type badge drop to a second line
            when the unit name eats the available horizontal space (lg
            grid cells get narrow at 1280px viewport). Pairs with the
            card's min-h so wrapped vs. inline badges produce the same
            tile height. */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight min-w-0">
            {device.unitNumber ?? '—'}
          </p>
          {/* Device-type badge — SALT in info/blue, Water Meter in
              secondary so the two are distinguishable at a glance. The
              auto-detector classifies on first transmission, so we
              suppress the badge entirely while `deviceType` is null
              rather than rendering a "Detecting…" placeholder. */}
          {device.deviceType === 'SALTTransmitter' && (
            <Badge variant="info" size="sm" className="flex-shrink-0">
              SALT
            </Badge>
          )}
          {device.deviceType === 'WaterMeter' && (
            <Badge variant="secondary" size="sm" className="flex-shrink-0">
              Water Meter
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs font-mono text-gray-500 dark:text-gray-400 truncate">
          {device.serialNumber}
        </p>
      </div>

      {/* Right-side area. Three states:
          - Waiting (not recognized): red dot + "Send Join Request" prompt.
          - Recognized via join, no fresh transmission yet: green dot + "Joined".
            Signal isn't shown — it would be carried over from a prior
            assignment, which misled testers ("signal showing without a
            join", batch-2 brainstorm).
          - Recognized AND has a transmission AT this unit: green dot +
            signal value + relative time. */}
      {isRecognized ? (
        isFresh ? (
          <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
            {device.latestTransmission?.rssi != null && (
              <SignalIcon
                rssi={device.latestTransmission.rssi}
                meta={{
                  snr: device.latestTransmission.snr,
                  spreadingFactor: device.latestTransmission.spreadingFactor,
                  gatewayEui: device.latestTransmission.gatewayEui,
                  timestamp: device.latestTransmission.timestamp ?? device.lastSeenAt,
                }}
              />
            )}
            {device.lastSeenAt && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                {formatRelativeTime(device.lastSeenAt)}
              </p>
            )}
          </div>
        ) : (
          <p className="flex-shrink-0 text-[11px] font-medium text-success-600 dark:text-success-400 whitespace-nowrap">
            Joined
          </p>
        )
      ) : (
        <p className="flex-shrink-0 text-[11px] font-medium text-error-600 dark:text-error-400 whitespace-nowrap">
          Send Join Request
        </p>
      )}
    </button>
  );
}
